import React, { useEffect, useRef } from "react";
import Companies from "../../../component/System/Companies";
import { connect } from "react-redux";
import { useNavigate, generatePath } from "react-router-dom";
import { loadingShow } from "../../../services/PostAPI";
import {
  fetchCompanies,
  initializeCompaniesScreen,
  setDeleteConfirmationPopupVisibility,
  saveCompaniesTableState,
  deleteCompanies,
} from "../../../redux/actions/System/Companies";
import { FILTER } from "../../../services/common/urlService";
import { DATE_STRATEGY, formatDate } from "../../../utils/date";
import { APP_URLS } from "../../../utils/constant";

function CompaniesScreen(props) {
  const {
    fetchCompanies,
    initCompaniesScreen,
    screenPermissions,
    showPageLevelLoader,
    companiesListView,
    paginationInfo,
    isLoading,
    savedTableState,
    saveCompaniesTableState,
    deleteCompanies,
    isCompaniesDeleteSuccess,
    isCompaniesDeleteError,
    shouldOpenDeleteConfirmationPopup,
    setDeleteConfirmationPopupVisibility,
  } = props;

  const navigate = useNavigate();

  // Use ref to capture initial savedTableState to avoid closure issues
  const initialSavedState = useRef(savedTableState);

  const buildFetchParamsFromSavedState = (state) => {
    if (!state) return null;

    // Helper to check if a string looks like an ISO date
    const isISODateString = (str) => {
      if (typeof str !== "string") return false;
      return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str);
    };

    // Helper to format date values to YYYY-MM-DD
    const formatFilterValue = (value) => {
      if (value instanceof Date) {
        return formatDate(value, DATE_STRATEGY.DATE_ISO);
      }
      if (isISODateString(value)) {
        return formatDate(new Date(value), DATE_STRATEGY.DATE_ISO);
      }
      return value;
    };

    // Build filter params in the format expected by the API
    const filters = [];
    if (state.filters) {
      Object.keys(state.filters).forEach((key) => {
        if (
          key !== "global" &&
          state.filters[key]?.value !== null &&
          state.filters[key]?.value !== undefined
        ) {
          const filterValue = state.filters[key].value;
          filters.push({
            filterField: state.filters[key].filterKey || key,
            matchStrategy:
              state.filters[key].matchStrategy || FILTER.MATCH_STRATEGY.CONTAINS,
            query: formatFilterValue(filterValue),
          });
        }
      });
    }

    return {
      sortField: state.sortField || "id",
      sortOrder: state.sortOrder,
      pageNumber: state.page || 0,
      pageSize: state.rows || 10,
      filters: filters.length > 0 ? filters : undefined,
    };
  };

  useEffect(() => {
    initCompaniesScreen();

    const savedParams = buildFetchParamsFromSavedState(initialSavedState.current);

    if (savedParams) {
      fetchCompanies(savedParams);
    } else {
      fetchCompanies({ sortField: "id", pageSize: 10 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLazyData = (props) => {
    fetchCompanies(props);
  };

  // Callback to save raw table state for restoration after navigation
  const handleTableStateChange = (rawParams) => {
    saveCompaniesTableState(rawParams);
  };

  const handleCreateCompany = () => {
    navigate(APP_URLS.SYSTEM_COMPANIES.CREATE);
  };

  const editCompanyRoute = (id) => {
    const path = generatePath(APP_URLS.SYSTEM_COMPANIES.EDIT, {
      id,
    });
    navigate(path);
  };

  const handleDeleteCompany = (payload) => {
    deleteCompanies({ id: payload.id, params: payload.params });
  };

  const companiesProps = {
    screenPermissions,
    setDeleteConfirmationPopupVisibility,
    fetchCompanies: loadLazyData,
    savedTableState,
    onTableStateChange: handleTableStateChange,
    handleCreateCompany,
    editCompanyRoute,
    onDeleteRecord: handleDeleteCompany,
    isCompaniesDeleteSuccess,
    isCompaniesDeleteError,
    shouldOpenDeleteConfirmationPopup,
    tableData: {
      columns: companiesListView?.columns,
      rows: companiesListView?.rows,
    },
    paginationInfo: {
      pageSize: paginationInfo?.pageSize,
      pageNumber: paginationInfo?.pageNumber,
      totalPages: paginationInfo?.totalPages,
      totalRecords: paginationInfo?.totalRecords,
    },
    isLoading,
  };

  if (showPageLevelLoader) {
    loadingShow("block");
    return null;
  }
  loadingShow("none");

  return (
    <div>
      <Companies {...companiesProps} />
    </div>
  );
}

const mapDispatchToProps = (dispatch) => {
  return {
    initCompaniesScreen: () => dispatch(initializeCompaniesScreen()),
    fetchCompanies: (payload) => dispatch(fetchCompanies(payload)),
    setDeleteConfirmationPopupVisibility: (payload) =>
      dispatch(setDeleteConfirmationPopupVisibility(payload)),
    saveCompaniesTableState: (payload) => dispatch(saveCompaniesTableState(payload)),
    deleteCompanies: (payload) => dispatch(deleteCompanies(payload)),
  };
};

const mapStateToProps = (state, ownProps) => {
  return {
    companiesListView: selectFromCompaniesReducer(state, "companiesListView"),
    showPageLevelLoader: selectFromCompaniesReducer(state, "isLoadingPage"),
    paginationInfo: selectFromCompaniesReducer(state, "paginationInfo"),
    isPageLevelError: selectFromCompaniesReducer(state, "isPageLevelError"),
    isLoading: selectFromCompaniesReducer(state, "isLoading"),
    savedTableState: selectFromCompaniesReducer(state, "savedTableState"),
    isCompaniesDeleteSuccess: selectFromCompaniesReducer(state, "isCompaniesDeleteSuccess"),
    isCompaniesDeleteError: selectFromCompaniesReducer(state, "isCompaniesDeleteError"),
    shouldOpenDeleteConfirmationPopup: selectFromCompaniesReducer(state, "shouldOpenDeleteConfirmationPopup"),
    ...ownProps,
  };
};

const selectFromCompaniesReducer = (state, path) => {
  return selectCompaniesReducer(state)[path];
};

const selectCompaniesReducer = ({ companiesReducer }) => {
  return companiesReducer;
};

export default connect(mapStateToProps, mapDispatchToProps)(CompaniesScreen);
